/**
 * Export sessione — build plan M1: "esporta sessione JSONL + Markdown on-demand (D3)".
 * Il JSONL della sessione = il sottoinsieme degli eventi con quel `session_id` (contratto
 * autonomo: riga per riga = §2.2). Il Markdown è il report leggibile di modello-dati §3.2,
 * generato dagli eventi (D3: artifact di export, non un dato che synca).
 *
 * I nomi degli esercizi arrivano dal catalogo (`programma.ts`) quando noti — lo slug
 * `exercise_id` è stabile per D14 esattamente per questo aggancio — con fallback sullo slug
 * (mai un nome inventato: se il catalogo non lo sa, l'ID è la verità).
 */
import type { HyprEvent } from "../storage/events";
import { toJsonl } from "./export";
import { statiSet, statoSessione, metricaIdenticiUltimo, type SetStato, type SessioneStato } from "./proiezione";
import { GIORNI, nomeEsercizioPerGiorno } from "../data/programma";
import { t } from "../i18n/it";

export function jsonlSessione(eventi: HyprEvent[], session_id: string): string {
  return toJsonl(eventi.filter((e) => e["session_id"] === session_id));
}

/**
 * Nome esercizio per l'export leggibile: catalogo CONTESTUALIZZATO al giorno della sessione
 * (i nomi-variante degli esercizi condivisi devono essere raggiungibili), altrimenti lo slug
 * (mai un nome inventato: se il catalogo non lo sa, l'ID è la verità).
 */
export function nomeLeggibile(exercise_id: string, day_label?: string): string {
  const giorno = day_label === undefined ? null : (GIORNI.find((g) => g.day_label === day_label) ?? null);
  return nomeEsercizioPerGiorno(giorno, exercise_id) ?? exercise_id;
}

/**
 * Colonna Kg (§3.2 "Kg"): `bodyweight` → "BW", `bodyweight_added` → "BW+5" (display del
 * contratto §1.5, fix m9), `barbell` → il carico. Deterministico, nessuna formattazione magica.
 */
export function caricoLeggibile(s: SetStato): string {
  if (s.load_type === "bodyweight") return t("loadBw");
  if (s.load_type === "bodyweight_added") return `${t("loadBwPlus")}${s.weight_kg}`;
  return String(s.weight_kg);
}

/**
 * Report Markdown della sessione (modello-dati §3.2). Tutto derivato dagli eventi: il file
 * regge da solo (anti-lock-in).
 */
export function markdownSessione(eventi: HyprEvent[], session_id: string): string {
  const sessione = statoSessione(eventi, session_id);
  if (sessione === null) return "";
  const sets = statiSet(eventi, session_id);
  const righe: string[] = [];
  righe.push(`# ${t("mdSessione")} ${sessione.date} — ${sessione.day_label}`);
  righe.push("");
  const contesto: string[] = [];
  if (sessione.weight_kg !== null) contesto.push(`${t("loadBw")} ${sessione.weight_kg}`);
  if (sessione.sleep_hours !== null) contesto.push(`${t("sonno")} ${sessione.sleep_hours}${t("unitH")}`);
  if (sessione.energy_level !== null) contesto.push(`${t("energia")} ${sessione.energy_level}${t("unitSu5")}`);
  if (sessione.duration_min !== null) contesto.push(`${t("mdDurata")} ${sessione.duration_min}'`);
  if (contesto.length > 0) {
    righe.push(`**${t("contesto")}**: ${contesto.join(" | ")}`);
    righe.push("");
  }
  righe.push(`**${t("mdStato")}**: ${sessione.status} · **session_id**: ${sessione.session_id}`);
  righe.push("");
  righe.push(
    `| ${t("mdColEsercizio")} | ${t("mdColSet")} | ${t("mdColReps")} | ${t("mdColKg")} | ${t("mdColRir")} | ${t("mdColNote")} |`,
  );
  righe.push("|---|---|---|---|---|---|");
  for (const s of sets) {
    const note = [s.is_warmup ? t("markWarmup") : "", s.corretto ? t("markCorretto") : "", s.notes].filter((x) => x !== "").join(" · ");
    righe.push(
      `| ${nomeLeggibile(s.exercise_id, sessione.day_label)} | ${s.set_number} | ${s.reps} | ${caricoLeggibile(s)} | ${s.rir ?? ""} | ${note} |`,
    );
  }
  if (sessione.notes !== "") {
    righe.push("");
    righe.push(`**${t("mdNoteSessione")}**: ${sessione.notes}`);
  }
  // metrica D51 (derivata, v. proiezione): la quota "identici all'ultimo" va nel report perché
  // il dogfooding la legga SENZA calcoli a mano — è una misura, non un giudizio
  const m = metricaIdenticiUltimo(eventi, session_id);
  if (m.confrontabili > 0) {
    righe.push("");
    righe.push(`**${t("metricaIdenticiTitolo")}**: ${m.identici}/${m.confrontabili}`);
  }
  righe.push("");
  return righe.join("\n");
}

/**
 * Nome file della sessione: `YYYY-MM-DD-<slug day_label>.<ext>` — data + giorno leggibile,
 * deterministico. Il `session_id` vive DENTRO i due file (riga di provenienza del MD e
 * contratto nel JSONL): il nome resta leggibile in Files/iCloud senza codici casuali.
 * Nota: due sessioni con la stessa data e lo stesso day_label condividono il nome — caso
 * limite, l'export è sempre un'azione esplicita dell'utente (nessun dato si perde in app).
 */
export function nomeFileSessione(sessione: SessioneStato, ext: "jsonl" | "md"): string {
  const data = sessione.date !== "" ? sessione.date : t("fileSenzaData");
  return `${data}-${slugGiorno(sessione.day_label)}.${ext}`;
}

/** Slug deterministico del day_label: minuscolo, non-alfanumerico → `-`, mai vuoto. */
export function slugGiorno(day_label: string): string {
  const slug = day_label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug !== "" ? slug : t("slugFallback");
}
